import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { ArrowLeft, Plus, Loader2, Edit, Calendar, BarChart3, MapPin, ExternalLink, Globe, Shield, CheckCircle2, Clock, XCircle, AlertTriangle, ShieldQuestion } from "lucide-react";
import type { Contact, Deal, ClientGroup, Partner, ContactAddress, Address } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContactFormModal } from "@/components/contact-form-modal";
import { DealFormModal } from "@/components/deal-form-modal";
import { LinkedDealsTable } from "@/components/linked-deals-table";
import { InlineTextField, InlineTextareaField } from "@/components/inline-edit";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { AddressPanel } from "@/components/address-panel";
import { EmploymentPanel } from "@/components/contacts/employment-panel";
import { LinkedAccountsPanel } from "@/components/contacts/linked-accounts-panel";
import ContactRolesPanel from "@/components/contacts/contact-roles-panel";
import { LivingExpensesPanel } from "@/components/financial/panels/living-expenses-panel";
import { PersonalFinancePanel } from "@/components/panels/personal-finance-panel";
import { EntityDocumentsPanel } from "@/components/documents/entity-documents-panel";
import { ActivityFeed } from "@/components/activity/activity-feed";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { buildNavigationUrl, buildBreadcrumbsFromUrl } from "@/lib/breadcrumbs";

// MT-004: Contact types match database schema
// Note: Contact type is auto-assigned and should not be manually edited
const CONTACT_TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "business_contact", label: "Business Contact" },
];

const CONTACT_TYPE_COLORS = {
  individual: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  business_contact: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
};

// ID Verification types
type IdVerificationStatus = "not_started" | "pending" | "verified" | "failed" | "expired";
type IdDocumentType = "drivers_licence" | "passport" | "medicare_card" | "proof_of_age" | "immicard" | "other";

interface VerificationData {
  contactId: string;
  idVerificationStatus: IdVerificationStatus;
  idDocumentType: IdDocumentType | null;
  idDocumentNumber: string | null;
  idDocumentExpiry: string | null;
  idVerifiedAt: string | null;
  idVerifiedBy: string | null;
}

const DOCUMENT_TYPE_LABELS: Record<IdDocumentType, string> = {
  drivers_licence: "Driver's Licence",
  passport: "Passport",
  medicare_card: "Medicare Card",
  proof_of_age: "Proof of Age",
  immicard: "ImmiCard",
  other: "Other",
};

const VERIFICATION_BADGE_CONFIG: Record<IdVerificationStatus, {
  label: string;
  icon: React.ReactNode;
  className: string;
}> = {
  not_started: {
    label: "Not Verified",
    icon: <ShieldQuestion className="h-3 w-3" />,
    className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700",
  },
  pending: {
    label: "Pending",
    icon: <Clock className="h-3 w-3" />,
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-700",
  },
  verified: {
    label: "Verified",
    icon: <CheckCircle2 className="h-3 w-3" />,
    className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-700",
  },
  failed: {
    label: "Failed",
    icon: <XCircle className="h-3 w-3" />,
    className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-700",
  },
  expired: {
    label: "Expired",
    icon: <AlertTriangle className="h-3 w-3" />,
    className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-700",
  },
};

function maskDocumentNumber(docNumber: string | null): string {
  if (!docNumber || docNumber.length <= 4) return docNumber ?? "-";
  return `••••${docNumber.slice(-4)}`;
}

function daysUntilExpiry(expiryDate: string | null): number | null {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// ---------------------------------------------------------------------------
// ID Verification Section Component
// ---------------------------------------------------------------------------

interface IdVerificationSectionProps {
  contactId: string;
}

function IdVerificationSection({ contactId }: IdVerificationSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editDocType, setEditDocType] = useState<IdDocumentType | "none">("none");
  const [editDocNumber, setEditDocNumber] = useState("");
  const [editExpiry, setEditExpiry] = useState("");

  const { data: verification, isLoading } = useQuery<VerificationData>({
    queryKey: ["/api/crm/contacts", contactId, "verification"],
    enabled: !!contactId,
  });

  // React Query v5: use useEffect for side effects, not onSuccess
  useEffect(() => {
    if (verification && isEditing) {
      setEditDocType((verification.idDocumentType as IdDocumentType) ?? "none");
      setEditDocNumber(verification.idDocumentNumber ?? "");
      setEditExpiry(verification.idDocumentExpiry ?? "");
    }
  }, [verification, isEditing]);

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      idVerificationStatus: IdVerificationStatus;
      idDocumentType?: IdDocumentType;
      idDocumentNumber?: string;
      idDocumentExpiry?: string;
    }) => {
      const response = await apiRequest("PATCH", `/api/crm/contacts/${contactId}/verification`, payload);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contactId, "verification"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contactId] });
      toast({ title: "Verification updated", description: "ID verification status saved." });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update verification status",
        variant: "destructive",
      });
    },
  });

  const handleStartEdit = () => {
    if (verification) {
      setEditDocType((verification.idDocumentType as IdDocumentType) ?? "none");
      setEditDocNumber(verification.idDocumentNumber ?? "");
      setEditExpiry(verification.idDocumentExpiry ?? "");
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    const payload: {
      idVerificationStatus: IdVerificationStatus;
      idDocumentType?: IdDocumentType;
      idDocumentNumber?: string;
      idDocumentExpiry?: string;
    } = {
      idVerificationStatus: "verified",
      idDocumentType: editDocType !== "none" ? editDocType : undefined,
      idDocumentNumber: editDocNumber || undefined,
      idDocumentExpiry: editExpiry || undefined,
    };
    updateMutation.mutate(payload);
  };

  const handleStatusChange = (newStatus: IdVerificationStatus) => {
    updateMutation.mutate({ idVerificationStatus: newStatus });
  };

  const isSaveDisabled =
    updateMutation.isPending ||
    !editDocNumber.trim() ||
    editDocType === "none";

  if (isLoading) {
    return (
      <Card data-testid="card-id-verification">
        <CardContent className="pt-2">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading verification...
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = verification?.idVerificationStatus ?? "not_started";
  const badgeConfig = VERIFICATION_BADGE_CONFIG[status];
  const daysLeft = daysUntilExpiry(verification?.idDocumentExpiry ?? null);
  const isNearExpiry = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;
  const isExpiredDoc = daysLeft !== null && daysLeft <= 0;

  return (
    <Card
      data-testid="card-id-verification"
      className={status === "verified" ? "border-l-4 border-l-green-500" : undefined}
    >
      <CardContent className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
            <Shield className="h-3 w-3" />
            ID VERIFICATION
          </h3>
          <Badge
            variant="outline"
            className={`text-xs flex items-center gap-1 ${badgeConfig.className}`}
            data-testid="badge-verification-status"
          >
            {badgeConfig.icon}
            {badgeConfig.label}
          </Badge>
        </div>

        {/* Expiry warnings */}
        {isNearExpiry && (
          <div
            className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mb-2"
            data-testid="alert-expiry-warning"
          >
            <AlertTriangle className="h-3 w-3" />
            Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
          </div>
        )}
        {(isExpiredDoc || status === "expired") && verification?.idDocumentExpiry && (
          <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 mb-2">
            <AlertTriangle className="h-3 w-3" />
            Document expired on {new Date(verification.idDocumentExpiry).toLocaleDateString("en-AU")}
          </div>
        )}

        {/* Edit form */}
        {isEditing ? (
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs">Document Type *</Label>
              <Select
                value={editDocType}
                onValueChange={(v) => setEditDocType(v as IdDocumentType | "none")}
              >
                <SelectTrigger data-testid="select-document-type" className="h-8 text-sm">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>Select document type</SelectItem>
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Document Number *</Label>
              <Input
                data-testid="input-document-number"
                value={editDocNumber}
                onChange={(e) => setEditDocNumber(e.target.value)}
                placeholder="e.g., NSW12345678"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Expiry Date</Label>
              <Input
                type="date"
                data-testid="input-document-expiry"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex gap-2 mt-1">
              <Button
                size="sm"
                disabled={isSaveDisabled}
                onClick={handleSave}
                data-testid="button-save-verify"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                )}
                Save & Verify
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(false)}
                data-testid="button-cancel-verification"
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          /* Display mode */
          <div className="space-y-2">
            {status === "not_started" && (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">No ID verification on file.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleStartEdit}
                  data-testid="button-start-verification"
                >
                  <Shield className="h-3 w-3 mr-1" />
                  Start Verification
                </Button>
              </div>
            )}
            {(status === "pending" || status === "verified") && verification?.idDocumentType && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Type</span>
                  <p>{DOCUMENT_TYPE_LABELS[verification.idDocumentType as IdDocumentType] ?? verification.idDocumentType}</p>
                </div>
                {verification.idDocumentNumber && (
                  <div>
                    <span className="text-xs text-muted-foreground">Number</span>
                    <p className="font-mono">{maskDocumentNumber(verification.idDocumentNumber)}</p>
                  </div>
                )}
                {verification.idDocumentExpiry && (
                  <div>
                    <span className="text-xs text-muted-foreground">Expiry</span>
                    <p>{new Date(verification.idDocumentExpiry).toLocaleDateString("en-AU")}</p>
                  </div>
                )}
                {verification.idVerifiedAt && (
                  <div>
                    <span className="text-xs text-muted-foreground">Verified</span>
                    <p>{new Date(verification.idVerifiedAt).toLocaleDateString("en-AU")}</p>
                  </div>
                )}
              </div>
            )}
            {status === "failed" && (
              <p className="text-sm text-muted-foreground">Verification failed. Please retry.</p>
            )}
            {(status === "expired" || isExpiredDoc) && verification?.idDocumentType && (
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {DOCUMENT_TYPE_LABELS[verification.idDocumentType as IdDocumentType] ?? verification.idDocumentType}
                </span>
              </div>
            )}
            {/* Action buttons per state */}
            <div className="flex gap-2 flex-wrap mt-1">
              {status === "pending" && (
                <>
                  <Button
                    size="sm"
                    onClick={handleStartEdit}
                    data-testid="button-mark-verified"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Mark as Verified
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive border-destructive hover:bg-destructive/10"
                    onClick={() => handleStatusChange("failed")}
                    data-testid="button-mark-failed"
                  >
                    Mark as Failed
                  </Button>
                </>
              )}
              {status === "verified" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleStartEdit}
                  data-testid="button-re-verify"
                >
                  Re-verify
                </Button>
              )}
              {(status === "failed") && (
                <Button
                  size="sm"
                  onClick={handleStartEdit}
                  data-testid="button-retry-verification"
                >
                  Retry Verification
                </Button>
              )}
              {(status === "expired" || isExpiredDoc) && (
                <Button
                  size="sm"
                  onClick={handleStartEdit}
                  data-testid="button-update-document"
                >
                  Update Document
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ContactDetail() {
  const [, params] = useRoute("/contacts/:id");
  const [_location, navigate] = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  // Use URL hash for tab persistence
  const [activeTab, setActiveTab] = useTabState("overview");

  const { toast } = useToast();

  // Parse search parameters for dynamic breadcrumbs
  // ISSUE-065: Use window.location.search instead of wouter's location (which only contains pathname)
  const searchParams = new URLSearchParams(window.location.search);

  const { data: contact, isLoading } = useQuery<Contact>({
    queryKey: ["/api/crm/contacts", params?.id],
    enabled: !!params?.id,
  });

  const { data: deals = [] } = useQuery<Deal[]>({
    queryKey: ['/api/crm/deals', { applicantId: params?.id, applicantType: 'contact' }],
    enabled: !!params?.id,
  });

  // Fetch addresses for this contact (uses default queryFn with auth headers)
  type AddressWithJunction = ContactAddress & { address: Address };
  const { data: addresses = [] } = useQuery<AddressWithJunction[]>({
    queryKey: ["/api/crm/contacts", params?.id, "addresses"],
    enabled: !!params?.id,
  });

  // Find primary address
  const primaryAddress = addresses.find(a => a.isPrimary === 1);

  // Fetch client group for this contact
  const { data: clientGroup } = useQuery<ClientGroup>({
    queryKey: ["/api/crm/client-groups", contact?.clientGroupId],
    enabled: !!contact?.clientGroupId,
  });

  // Fetch referring partner for this contact
  const { data: referringPartner } = useQuery<Partner>({
    queryKey: ["/api/crm/partners", contact?.partnerId],
    enabled: !!contact?.partnerId,
  });
  const referringPartners = referringPartner ? [referringPartner] : [];

  // Fetch roles count for badge (uses default queryFn with auth headers)
  type ContactRolesSummary = { summary: { totalActiveRoles: number } };
  const { data: rolesData } = useQuery<ContactRolesSummary>({
    queryKey: ["/api/crm/contacts", params?.id, "roles"],
    enabled: !!params?.id,
  });
  const rolesCount = rolesData?.summary?.totalActiveRoles || 0;

  // Fetch verification status for header badge (F007 Sprint 26)
  const { data: verificationData } = useQuery<VerificationData>({
    queryKey: ["/api/crm/contacts", params?.id, "verification"],
    enabled: !!params?.id,
  });
  const headerVerifStatus = verificationData?.idVerificationStatus ?? "not_started";
  const headerVerifBadge = VERIFICATION_BADGE_CONFIG[headerVerifStatus];

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Contact>) => {
      const response = await apiRequest("PATCH", `/api/crm/contacts/${params?.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/contacts", params?.id] });
      const previousContact = queryClient.getQueryData(["/api/crm/contacts", params?.id]);
      if (previousContact) {
        queryClient.setQueryData(["/api/crm/contacts", params?.id], {
          ...previousContact,
          ...updates,
        });
      }
      return { previousContact };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousContact) {
        queryClient.setQueryData(["/api/crm/contacts", params?.id], context.previousContact);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", params?.id] });
    },
  });

  const handleFieldUpdate = async (field: keyof Contact, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };





  const handleNavItemClick = (href: string) => {
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading contact...</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The contact you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/contacts")} size="sm">Back to Contacts</Button>
        </div>
      </div>
    );
  }

  const fullName = [contact.firstName, contact.middleName, contact.lastName]
    .filter(Boolean)
    .join(" ");

  // Build dynamic breadcrumbs based on navigation history
  const breadcrumbs = buildBreadcrumbsFromUrl(
    searchParams,
    [
      { label: "Home", href: "/" },
      { label: "Contacts", href: "/contacts" },
      { label: fullName || "Contact" },
    ]
  );

  return (
    <>
      <DetailPageShell
        title={fullName || "Contact"}
        breadcrumbs={breadcrumbs}
        value={activeTab}
        onValueChange={setActiveTab}
        onNavItemClick={handleNavItemClick}
        badge={
          <div className="flex items-center gap-1.5">
            <Badge
              variant="secondary"
              className={`capitalize text-xs ${CONTACT_TYPE_COLORS[contact.contactType as keyof typeof CONTACT_TYPE_COLORS] || CONTACT_TYPE_COLORS.individual}`}
            >
              {contact.contactType?.replace("_", " ") || "Individual"}
            </Badge>
            <Badge
              variant="outline"
              className={`text-xs flex items-center gap-1 ${headerVerifBadge.className}`}
              data-testid="badge-verification-status"
            >
              {headerVerifBadge.icon}
              {headerVerifBadge.label}
            </Badge>
            {contact.archivedAt && <span role="status" aria-label="Record status">Archived</span>}
          </div>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Accounts", href: "#accounts" },
          { label: "Roles", href: "#roles", count: rolesCount },
          { label: "Deals", href: "#deals", count: deals.length },
          { label: "Financials", href: "#financials" },
          { label: "Documents", href: "#documents" },
          { label: "Communications", href: "#communications" },
          { label: "Activity", href: "#activity" },
        ]}
        linkedEntities={[
          { label: "Accounts", count: 0, href: "#accounts" },
          { label: "Deals", count: deals.length, href: "#deals" },
          { label: "Documents", count: 0, href: "#documents" },
        ]}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setIsArchiveDialogOpen(true)}>
              {contact.archivedAt ? "Restore Contact" : "Archive Contact"}
            </Button>
          <Button variant="outline" size="sm" onClick={() => {
            if (sessionStorage.getItem('cameFromApp') === 'true') {
              window.history.back();
            } else {
              navigate("/contacts");
            }
          }} data-testid="button-back">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          </>
        }
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div id="overview" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CORE DETAILS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={contact.firstName}
                        onSave={(value) => handleFieldUpdate("firstName", value)}
                        label="First Name"
                        placeholder="Enter first name"
                        testId="input-first-name"
                      />
                      <InlineTextField
                        value={contact.middleName}
                        onSave={(value) => handleFieldUpdate("middleName", value || null)}
                        label="Middle Name"
                        placeholder="Optional"
                        testId="input-middle-name"
                      />
                      <InlineTextField
                        value={contact.lastName}
                        onSave={(value) => handleFieldUpdate("lastName", value)}
                        label="Last Name"
                        placeholder="Enter last name"
                        testId="input-last-name"
                      />
                      <InlineTextField
                        value={contact.preferredName}
                        onSave={(value) => handleFieldUpdate("preferredName", value || null)}
                        label="Preferred Name"
                        placeholder="Optional"
                        testId="input-preferred-name"
                      />
                      {/* MT-004: Contact type is auto-assigned, not editable */}
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-muted-foreground">Contact Type</label>
                        <div className="flex items-center h-9">
                          <span className="text-sm">
                            {CONTACT_TYPE_OPTIONS.find(opt => opt.value === contact.contactType)?.label || "Individual"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CONTACT INFORMATION</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={contact.email}
                        onSave={(value) => handleFieldUpdate("email", value || null)}
                        label="Email"
                        placeholder="Optional"
                        testId="input-email"
                      />
                      <div className="space-y-1">
                        <InlineTextField
                          value={contact.mobile}
                          onSave={(value) => handleFieldUpdate("mobile", value || null)}
                          label="Mobile"
                          placeholder="Optional"
                          testId="input-mobile"
                        />
                        {contact.isOverseasMobile && contact.mobile && (
                          <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                            <Globe className="h-3 w-3" />
                            <span>International mobile</span>
                          </div>
                        )}
                      </div>
                      <InlineTextField
                        value={contact.phone}
                        onSave={(value) => handleFieldUpdate("phone", value || null)}
                        label="Phone"
                        placeholder="Optional"
                        testId="input-phone"
                      />
                      <InlineTextField
                        value={contact.linkedin}
                        onSave={(value) => handleFieldUpdate("linkedin", value || null)}
                        label="LinkedIn"
                        placeholder="https://linkedin.com/in/..."
                        testId="input-linkedin"
                        formatDisplay={(val) =>
                          val ? (
                            <a
                              href={val.startsWith("http") ? val : `https://${val}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {val}
                            </a>
                          ) : (
                            "-"
                          )
                        }
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* ID Verification Section — F007 Sprint 26 */}
                <IdVerificationSection contactId={contact.id} />

                <Card>
                  <CardContent className="pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-muted-foreground">PRIMARY ADDRESS</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveTab("addresses")}
                        className="h-6 text-xs"
                      >
                        <MapPin className="h-3 w-3 mr-1" />
                        Manage Addresses
                      </Button>
                    </div>
                    {primaryAddress ? (
                      <div className="space-y-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {primaryAddress.address.formattedAddress || [
                                primaryAddress.address.unitNumber && `${primaryAddress.address.unitNumber}/`,
                                primaryAddress.address.streetNumber,
                                primaryAddress.address.streetName,
                              ].filter(Boolean).join("")}
                            </p>
                            {!primaryAddress.address.formattedAddress && (
                              <p className="text-sm text-muted-foreground">
                                {[
                                  primaryAddress.address.suburb,
                                  primaryAddress.address.state,
                                  primaryAddress.address.postcode,
                                ].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs shrink-0 ml-2">
                            {primaryAddress.addressType?.replace("_", " ") || "Unknown"}
                          </Badge>
                        </div>
                        {primaryAddress.address.isVerified === 1 && (
                          <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                            <MapPin className="h-3 w-3" />
                            <span>Google verified</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-6 text-center border rounded-md border-dashed">
                        <MapPin className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No primary address set</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setActiveTab("addresses")}
                          className="text-xs mt-1"
                        >
                          Add address in Addresses tab
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ADDITIONAL</h3>
                    <div className="space-y-2">
                      <InlineTextField
                        value={contact.leadSource}
                        onSave={(value) => handleFieldUpdate("leadSource", value || null)}
                        label="Lead Source"
                        placeholder="Optional"
                        testId="input-lead-source"
                      />
                      <InlineTextareaField
                        value={contact.notes}
                        onSave={(value) => handleFieldUpdate("notes", value || null)}
                        label="Notes"
                        placeholder="Add notes..."
                        testId="textarea-notes"
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">REFERRING PARTNER</h3>
                    {referringPartners.length > 0 ? (
                      <div className="space-y-2">
                        {referringPartners.map((partner) => (
                          <div key={partner.id} className="flex items-center justify-between p-2 rounded border bg-muted/30">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{partner.partnerName}</span>
                                {partner.partnerType && (
                                  <Badge variant="outline" className="text-xs">
                                    {partner.partnerType.replace(/_/g, " ")}
                                  </Badge>
                                )}
                              </div>
                              {partner.email && (
                                <span className="text-xs text-muted-foreground">{partner.email}</span>
                              )}
                            </div>
                            <Link href={`/partners/${partner.id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No referring partner linked</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "addresses",
            label: "Addresses",
            content: (
              <div id="addresses" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <AddressPanel 
                      entityType="contact" 
                      entityId={contact.id}
                      data-testid="panel-contact-addresses"
                    />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "employment",
            label: "Employment",
            content: (
              <div id="employment" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <EmploymentPanel contactId={contact.id} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "accounts",
            label: "Accounts",
            content: (
              <div id="accounts" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <LinkedAccountsPanel contact={contact} />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "roles",
            label: "Roles",
            content: (
              <div id="roles" className="space-y-2">
                <ContactRolesPanel contactId={contact.id} contactLabel={fullName} />
              </div>
            ),
          },
          {
            value: "deals",
            label: "Deals",
            content: (
              <div id="deals" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">Deals</h3>
                      <Button
                        onClick={() => setIsDealModalOpen(true)}
                        size="sm"
                        data-testid="button-add-deal-tab"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Deal
                      </Button>
                    </div>
                    <LinkedDealsTable
                      applicantId={contact.id}
                      applicantType="contact"
                      sourceFrom={{ from: 'contact', fromId: contact.id, fromLabel: fullName }}
                    />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "financials",
            label: "Financials",
            content: (
              <div id="financials" className="space-y-2">
                <LivingExpensesPanel contactId={contact.id} />
                <PersonalFinancePanel contactId={contact.id} />
              </div>
            ),
          },
          {
            value: "documents",
            label: "Documents",
            content: (
              <div id="documents" className="space-y-2">
                <EntityDocumentsPanel entityId={contact.id} entityType="contact" />
              </div>
            ),
          },
          {
            value: "communications",
            label: "Communications",
            content: (
              <div id="communications" className="space-y-2">
                <ActivityFeed
                  entityType="contact"
                  entityId={contact.id}
                  lens="email"
                  testId="panel-contact-communications"
                />
              </div>
            ),
          },
          {
            value: "activity",
            label: "Activity",
            content: (
              <div id="activity" className="space-y-2">
                <ActivityFeed
                  entityType="contact"
                  entityId={contact.id}
                  testId="panel-contact-activity"
                />
              </div>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4">
            {/* Key Dates - MUST be first (metadata) */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                KEY DATES
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>
                    {contact.createdAt
                      ? new Date(contact.createdAt).toLocaleDateString("en-AU")
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span>
                    {contact.updatedAt
                      ? new Date(contact.updatedAt).toLocaleDateString("en-AU")
                      : "-"}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                QUICK STATS
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deals</span>
                  <span>{deals.length}</span>
                </div>
              </div>
            </div>

            {/* Client Group */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CLIENT GROUP</h3>
              {clientGroup ? (
                <button
                  onClick={() => {
                    const url = buildNavigationUrl(`/client-groups/${clientGroup.id}`, {
                      from: 'contact',
                      fromId: params?.id || '',
                      fromLabel: fullName || 'Contact',
                    });
                    navigate(url);
                  }}
                  className="text-sm text-primary hover:underline font-medium"
                  data-testid="link-client-group"
                >
                  {clientGroup.groupName}
                </button>
              ) : (
                <p className="text-sm text-muted-foreground">-</p>
              )}
            </div>

            {/* Actions */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACTIONS</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setIsEditModalOpen(true)}
                  data-testid="button-edit-contact"
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Contact
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setIsDealModalOpen(true)}
                  data-testid="button-new-deal"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Deal
                </Button>
              </div>
            </div>
          </div>
        }
      />

      <ContactFormModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        contact={contact}
      />

      <DealFormModal
        open={isDealModalOpen}
        onOpenChange={setIsDealModalOpen}
        applicantId={contact.id}
        applicantType="contact"
      />

      <CoreArchiveDialog entity="contact" record={contact} open={isArchiveDialogOpen}
      onOpenChange={setIsArchiveDialogOpen} />
    </>
  );
}
